import { useMemo } from 'react';

import * as yup from 'yup';

import { useFeatureFlag } from '@order/LaunchDarkly';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Returns the "too-far" threshold for the target location.
 *
 * - Delivery uses a fixed one-mile threshold, while pickup uses the matching configured market area.
 * - The pickup LaunchDarkly value is validated with yup before any market-area data is used.
 * - Each market-area key must contain a positive `distanceInMiles` and a four-item `mapBounds`
 *   tuple ordered as `[topLeftLatitude, topLeftLongitude, bottomRightLatitude, bottomRightLongitude]`.
 * - Invalid configuration or pickup coordinates outside the configured bounds return 0 without throwing.
 * - Outpost returns 0 and does not use the "too-far" check.
 * - Returns 0 when the "too-far" check should be disabled.
 */
export const useTooFarLocationThresholdInMiles = (params: Params) => {
  const { channel, targetPosition } = params;

  const pickupMarketAreaConfig = useFeatureFlag('CELS-5744-order-pickup-market-area-too-far-config'); // prettier-ignore

  const targetLatitude = targetPosition?.latitude;
  const targetLongitude = targetPosition?.longitude;
  const hasTargetPosition = targetLatitude !== undefined && targetLongitude !== undefined;

  // ─────────────────────────────────────────────────────────────────────

  return useMemo(() => {
    const canEvaluateThreshold = hasTargetPosition && channel !== 'outpost';

    if (!canEvaluateThreshold) {
      return 0;
    }

    return getTooFarLocationThresholdInMiles({
      channel,
      pickupMarketAreaConfig,
      targetPosition: { latitude: targetLatitude, longitude: targetLongitude },
    });
  }, [channel, hasTargetPosition, pickupMarketAreaConfig, targetLatitude, targetLongitude]);
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function getTooFarLocationThresholdInMiles(params: GetThresholdParams) {
  const { channel, pickupMarketAreaConfig, targetPosition } = params;

  if (channel === 'delivery') {
    return DELIVERY_TOO_FAR_THRESHOLD_IN_MILES;
  }

  const canUsePickupMarketAreaConfig = channel === 'pickup';

  if (!canUsePickupMarketAreaConfig) {
    return 0;
  }

  const marketAreas = getValidMarketAreas(pickupMarketAreaConfig);
  const matchingMarketArea = marketAreas.find(({ mapBounds }) =>
    checkIfPositionWithinMapBounds(targetPosition, mapBounds),
  );

  return matchingMarketArea?.distanceInMiles ?? 0;
}

function getValidMarketAreas(config: unknown) {
  const isConfigObject = marketAreaConfigSchema.isValidSync(config, STRICT_VALIDATION);

  if (!isConfigObject) {
    return [];
  }

  const hasEmptyMarketAreaKey = Object.keys(config).some(checkIfEmptyKey);

  if (hasEmptyMarketAreaKey) {
    return [];
  }

  const marketAreas = Object.values(config);
  const areMarketAreasValid = marketAreasSchema.isValidSync(marketAreas, STRICT_VALIDATION);

  if (!areMarketAreasValid) {
    return [];
  }

  return marketAreas;
}

function checkIfEmptyKey(key: string) {
  return key.length === 0;
}

function checkIfMapBoundsOrdered(mapBounds: MapBounds) {
  const [topLeftLatitude, topLeftLongitude, bottomRightLatitude, bottomRightLongitude] = mapBounds;

  return topLeftLatitude >= bottomRightLatitude && topLeftLongitude <= bottomRightLongitude;
}

function checkIfPositionWithinMapBounds(position: PositionCoordinates, mapBounds: MapBounds) {
  const { latitude, longitude } = position;

  const [topLeftLatitude, topLeftLongitude, bottomRightLatitude, bottomRightLongitude] = mapBounds;

  const isWithinLatitudeBounds = latitude <= topLeftLatitude && latitude >= bottomRightLatitude;
  const isWithinLongitudeBounds = longitude >= topLeftLongitude && longitude <= bottomRightLongitude; // prettier-ignore

  return isWithinLatitudeBounds && isWithinLongitudeBounds;
}

// ─── Constants ───────────────────────────────────────────────────────────────

const DELIVERY_TOO_FAR_THRESHOLD_IN_MILES = 1;

// Strict mode rejects values yup would otherwise coerce, such as "5" for a number
const STRICT_VALIDATION = { strict: true };

// ─── Schema ──────────────────────────────────────────────────────────────────

const latitudeSchema = yup.number().required().min(-90).max(90);
const longitudeSchema = yup.number().required().min(-180).max(180);

const mapBoundsTupleSchema = yup
  .tuple([latitudeSchema, longitudeSchema, latitudeSchema, longitudeSchema])
  .required();

const mapBoundsSchema = mapBoundsTupleSchema.test(
  'ordered-map-bounds',
  'Map bounds must run from the top-left to the bottom-right corner',
  checkIfMapBoundsOrdered,
);

const marketAreaSchema = yup.object({
  mapBounds: mapBoundsSchema,
  distanceInMiles: yup.number().required().positive(),
});

const marketAreasSchema = yup.array().of(marketAreaSchema.required()).required();

const marketAreaConfigSchema = yup.object().required();

// ─── Types ───────────────────────────────────────────────────────────────────

type Params = {
  channel?: OrderChannel;
  targetPosition?: PositionCoordinates;
};

type GetThresholdParams = Omit<Params, 'targetPosition'> & {
  pickupMarketAreaConfig: unknown;
  targetPosition: PositionCoordinates;
};

type OrderChannel = 'delivery' | 'outpost' | 'pickup';
type MapBounds = yup.InferType<typeof mapBoundsTupleSchema>;

type PositionCoordinates = {
  latitude: number;
  longitude: number;
};
